import { Flex, Popover, SegmentedControl, Slider, Switch } from '@byecode/ui'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import {
    FilledInput,
    FillPickerPopoverDropdown,
    getImageFullUrlInApplication,
    Group4ByecodeUi,
    IconPicker,
    ListItem4ByecodeUi
} from '@lighthouse/shared'
import { Controller, useFormContext } from 'react-hook-form'

import { InheritLabel } from '@/components/InheritLabel'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { uploadManagerInAppParams } from '@/utils/auth'

const POSITION_OPTIONS = [
    {
        label: '居中',
        value: 'center'
    },
    {
        label: '居上',
        value: 'top'
    }
]

export const TreeSetting = () => {
    const { control } = useFormContext()
    const { handleSetBreakKey } = useSetExtendsKeys()
    const colorSystemMethods = useColorSystemAction()
    const appId = useCurrentAppId()

    return (
        <Group4ByecodeUi label="展开/收起控件">
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="每级缩进" name="breakPoint.tree.indent" />
                <Controller
                    control={control}
                    name="breakPoint.tree.indent"
                    defaultValue={28}
                    render={({ field }) => (
                        <Slider
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey('breakPoint.tree.indent')
                            }}
                            style={{ width: 180 }}
                            min={0}
                            max={100}
                            step={1}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="对齐" name="breakPoint.tree.align" />
                <Controller
                    control={control}
                    name="breakPoint.tree.align"
                    render={({ field }) => (
                        <SegmentedControl
                            style={{ width: 180 }}
                            fullWidth
                            data={POSITION_OPTIONS}
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey('breakPoint.tree.align')
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="上边距" name="breakPoint.tree.topMargin" />
                <Controller
                    control={control}
                    name="breakPoint.tree.topMargin"
                    render={({ field }) => (
                        <Slider
                            value={field.value}
                            onChange={val => field.onChange(val)}
                            style={{ width: 180 }}
                            min={0}
                            max={100}
                            step={1}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="当展开时" name="breakPoint.tree.expandIcon" />
                <Flex style={{ width: 180 }} gap={8}>
                    <Controller
                        control={control}
                        name="breakPoint.tree.expandIcon.color"
                        render={({ field }) => {
                            const value = field.value && {
                                type: field.value.type,
                                color: field.value.color,
                                gradient: field.value.gradient
                            }
                            return (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={value}
                                            onClear={() => {
                                                field.onChange({
                                                    ...field.value,
                                                    type: BACKGROUND_TYPE.color,
                                                    color: ''
                                                })
                                            }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )
                        }}
                    />
                    <Controller
                        control={control}
                        name="breakPoint.tree.expandIcon.icon"
                        render={({ field }) => (
                            <IconPicker
                                clearable
                                value={field.value}
                                uploadParams={uploadManagerInAppParams()}
                                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Flex>
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="当收起时" name="breakPoint.tree.collapseIcon" />
                <Flex style={{ width: 180 }} gap={8}>
                    <Controller
                        control={control}
                        name="breakPoint.tree.collapseIcon.color"
                        render={({ field }) => {
                            const value = field.value && {
                                type: field.value.type,
                                color: field.value.color,
                                gradient: field.value.gradient
                            }
                            return (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={value}
                                            onClear={() => {
                                                field.onChange({
                                                    ...field.value,
                                                    type: BACKGROUND_TYPE.color,
                                                    color: ''
                                                })
                                            }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )
                        }}
                    />
                    <Controller
                        control={control}
                        name="breakPoint.tree.collapseIcon.icon"
                        render={({ field }) => (
                            <IconPicker
                                clearable
                                value={field.value}
                                uploadParams={uploadManagerInAppParams()}
                                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Flex>
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="默认全部展开" name="breakPoint.tree.defaultExpandAll" />
                <Controller
                    control={control}
                    name="breakPoint.tree.defaultExpandAll"
                    defaultValue
                    render={({ field }) => <Switch checked={field.value} onChange={val => field.onChange(val)} />}
                />
            </ListItem4ByecodeUi>
        </Group4ByecodeUi>
    )
}
